import { z } from 'zod';
import { BIBLE_TOTAL_CHAPTERS, compareBibleChapterIds, isBibleChapterId, isValidBibleBookOrder } from '../bibleCanon';

const instantSchema = z.iso.datetime({ offset: true });
const localDateSchema = z.iso.date();
const entityIdSchema = z.string().min(1);
export const documentSha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'SHA-256 do documento inválido.');
export const epubCfiSchema = z.string().min(10).max(2048).superRefine((value, context) => {
  const containsControlCharacter = [...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
  });
  if (new TextEncoder().encode(value).byteLength > 4096
    || containsControlCharacter
    || !/^epubcfi\([^()\s]+\)$/.test(value)) {
    context.addIssue({ code: 'custom', message: 'CFI EPUB inválido.' });
  }
});

export const bookStatusSchema = z.enum([
  'want_to_read',
  'reading',
  'finished',
  'paused',
  'abandoned'
]);

export const pageProgressSchema = z.strictObject({
  kind: z.literal('pages'),
  currentPage: z.int().min(0),
  totalPages: z.int().positive().optional()
}).superRefine((value, context) => {
  if (value.totalPages !== undefined && value.currentPage > value.totalPages) {
    context.addIssue({ code: 'custom', path: ['currentPage'], message: 'A página atual não pode ultrapassar o total.' });
  }
});

export const audioTimeProgressSchema = z.strictObject({
  kind: z.literal('audio_time'),
  currentMinutes: z.int().min(0),
  totalMinutes: z.int().positive()
}).superRefine((value, context) => {
  if (value.currentMinutes > value.totalMinutes) {
    context.addIssue({ code: 'custom', path: ['currentMinutes'], message: 'A posição não pode ultrapassar a duração.' });
  }
});

export const audioPercentProgressSchema = z.strictObject({
  kind: z.literal('audio_percent'),
  currentPercent: z.number().min(0).max(100)
});

const bibleChapterIdSchema = z.string().refine(isBibleChapterId, 'Capítulo bíblico inválido.');
const uniqueCanonicalChapterIdsSchema = z.array(bibleChapterIdSchema).superRefine((ids, context) => {
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: 'custom', message: 'Capítulos bíblicos não podem se repetir.' });
  }
  const sorted = [...ids].sort(compareBibleChapterIds);
  if (ids.some((id, index) => id !== sorted[index])) {
    context.addIssue({ code: 'custom', message: 'Capítulos bíblicos precisam estar em ordem canônica.' });
  }
});

export const bibleChapterProgressSchema = z.strictObject({
  kind: z.literal('bible_chapters'),
  completedChapterIds: uniqueCanonicalChapterIdsSchema,
  totalPages: z.int().positive().optional()
});

export const epubProgressSchema = z.strictObject({
  kind: z.literal('epub'),
  documentSha256: documentSha256Schema,
  currentCfi: epubCfiSchema.optional(),
  currentPercent: z.number().min(0).max(100)
});

export const readingProgressSchema = z.union([
  pageProgressSchema,
  audioTimeProgressSchema,
  audioPercentProgressSchema,
  bibleChapterProgressSchema,
  epubProgressSchema
]);

export const completionDateSchema = z.discriminatedUnion('precision', [
  z.strictObject({ precision: z.literal('day'), date: localDateSchema }),
  z.strictObject({ precision: z.literal('month'), year: z.int().min(1000).max(3000), month: z.int().min(1).max(12) }),
  z.strictObject({ precision: z.literal('year'), year: z.int().min(1000).max(3000) }),
  z.strictObject({ precision: z.literal('unknown') })
]);

const manualReadingTimeSchema = z.strictObject({
  source: z.literal('manual'),
  durationMs: z.int().positive(),
  startedAt: instantSchema.optional(),
  finishedAt: instantSchema.optional()
}).superRefine((readingTime, context) => {
  const hasStartedAt = readingTime.startedAt !== undefined;
  const hasFinishedAt = readingTime.finishedAt !== undefined;
  if (hasStartedAt !== hasFinishedAt) {
    context.addIssue({ code: 'custom', path: hasStartedAt ? ['finishedAt'] : ['startedAt'], message: 'Os horários inicial e final precisam existir juntos.' });
    return;
  }
  if (!readingTime.startedAt || !readingTime.finishedAt) return;
  const startedMs = Date.parse(readingTime.startedAt);
  const finishedMs = Date.parse(readingTime.finishedAt);
  if (finishedMs <= startedMs) {
    context.addIssue({ code: 'custom', path: ['finishedAt'], message: 'O horário final precisa ser posterior ao horário inicial.' });
  } else if (readingTime.durationMs !== finishedMs - startedMs) {
    context.addIssue({ code: 'custom', path: ['durationMs'], message: 'A duração precisa corresponder ao intervalo informado.' });
  }
});

export const readingTimeContributionSchema = z.discriminatedUnion('source', [
  manualReadingTimeSchema,
  z.strictObject({
    source: z.literal('estimated'),
    durationMs: z.int().positive(),
    pagesPerHour: z.number().positive(),
    sampleCount: z.int().min(2)
  })
]);

const progressEntryBaseShape = {
  id: entityIdSchema,
  bookId: entityIdSchema,
  source: z.enum(['baseline', 'manual', 'session', 'correction']),
  isBaseline: z.literal(true).optional(),
  sessionId: entityIdSchema.optional(),
  recordedAt: instantSchema,
  occurredOn: localDateSchema,
  supersededByEntryId: entityIdSchema.optional(),
  correctionOfEntryIds: z.array(entityIdSchema).optional(),
  readingTime: readingTimeContributionSchema.optional(),
  createdAt: instantSchema
};

export const readingProgressEntrySchema = z.discriminatedUnion('kind', [
  z.strictObject({
    ...progressEntryBaseShape,
    kind: z.literal('pages'),
    previousPage: z.int().min(0),
    currentPage: z.int().min(0),
    deltaPages: z.int().min(0)
  }),
  z.strictObject({
    ...progressEntryBaseShape,
    kind: z.literal('audio_time'),
    previousMinutes: z.int().min(0),
    currentMinutes: z.int().min(0),
    deltaMinutes: z.int().min(0)
  }),
  z.strictObject({
    ...progressEntryBaseShape,
    kind: z.literal('audio_percent'),
    previousPercent: z.number().min(0).max(100),
    currentPercent: z.number().min(0).max(100),
    deltaPercent: z.number().min(0).max(100)
  }),
  z.strictObject({
    ...progressEntryBaseShape,
    kind: z.literal('bible_chapters'),
    chapterIds: uniqueCanonicalChapterIdsSchema,
    startPage: z.int().min(0),
    endPage: z.int().min(0),
    pagesRead: z.int().min(0)
  }),
  z.strictObject({
    ...progressEntryBaseShape,
    kind: z.literal('epub'),
    documentSha256: documentSha256Schema,
    previousCfi: epubCfiSchema.optional(),
    currentCfi: epubCfiSchema.optional(),
    previousPercent: z.number().min(0).max(100),
    currentPercent: z.number().min(0).max(100),
    deltaPercent: z.number().min(0).max(100)
  })
]).superRefine((entry, context) => {
  if (entry.source === 'session' && !entry.sessionId) {
    context.addIssue({ code: 'custom', path: ['sessionId'], message: 'Progresso de sessão precisa identificar a sessão.' });
  }
  if ((entry.source === 'baseline') !== Boolean(entry.isBaseline)) {
    context.addIssue({ code: 'custom', path: ['isBaseline'], message: 'A marca de baseline deve corresponder à origem da entrada.' });
  }
  if (entry.isBaseline) {
    const delta = entry.kind === 'pages'
      ? entry.deltaPages
      : entry.kind === 'audio_time'
        ? entry.deltaMinutes
        : entry.kind === 'bible_chapters'
          ? entry.pagesRead
          : entry.deltaPercent;
    if (delta !== 0) context.addIssue({ code: 'custom', message: 'Baseline não pode contribuir para estatísticas.' });
  }
  if (entry.supersededByEntryId === entry.id || entry.correctionOfEntryIds?.includes(entry.id)) {
    context.addIssue({ code: 'custom', path: ['supersededByEntryId'], message: 'Uma entrada não pode corrigir a si mesma.' });
  }
  const expectedDelta = entry.kind === 'pages'
    ? Math.max(0, entry.currentPage - entry.previousPage)
    : entry.kind === 'audio_time'
      ? Math.max(0, entry.currentMinutes - entry.previousMinutes)
      : entry.kind === 'audio_percent' || entry.kind === 'epub'
        ? Math.max(0, entry.currentPercent - entry.previousPercent)
        : entry.endPage - entry.startPage;
  const actualDelta = entry.kind === 'pages'
    ? entry.deltaPages
    : entry.kind === 'audio_time'
      ? entry.deltaMinutes
      : entry.kind === 'bible_chapters'
        ? entry.pagesRead
        : entry.deltaPercent;
  if (actualDelta !== expectedDelta) context.addIssue({ code: 'custom', message: 'O delta não corresponde aos valores anterior e atual.' });
  if (entry.kind === 'bible_chapters') {
    if (entry.endPage < entry.startPage) context.addIssue({ code: 'custom', path: ['endPage'], message: 'A página final não pode ser menor que a inicial.' });
    const administrative = entry.isBaseline
      || (entry.source === 'correction' && entry.chapterIds.length === 0 && !entry.readingTime);
    if (administrative) {
      if (entry.startPage !== 0 || entry.endPage !== 0 || entry.pagesRead !== 0) {
        context.addIssue({ code: 'custom', path: ['pagesRead'], message: 'Baseline e tombstone bíblicos devem usar páginas 0 → 0.' });
      }
    } else {
      if (entry.chapterIds.length === 0) context.addIssue({ code: 'custom', path: ['chapterIds'], message: 'Informe ao menos um capítulo lido.' });
      if (entry.endPage <= entry.startPage) context.addIssue({ code: 'custom', path: ['endPage'], message: 'A página final precisa ser maior que a inicial.' });
    }
  }
  if (entry.readingTime && !['manual', 'correction'].includes(entry.source)) {
    context.addIssue({ code: 'custom', path: ['readingTime'], message: 'Tempo não cronometrado só pode acompanhar leitura manual ou sua correção.' });
  }
  if (entry.readingTime && actualDelta <= 0) {
    context.addIssue({ code: 'custom', path: ['readingTime'], message: 'Tempo não cronometrado exige progresso positivo.' });
  }
  if (entry.readingTime?.source === 'estimated' && entry.kind !== 'pages') {
    context.addIssue({ code: 'custom', path: ['readingTime'], message: 'Estimativa automática é permitida apenas para livros paginados.' });
  }
});

const readingSessionBaseShape = {
  id: entityIdSchema,
  bookId: entityIdSchema,
  startedAt: instantSchema,
  startedOn: localDateSchema,
  accumulatedPausedMs: z.number().min(0),
  startProgress: readingProgressSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema
};

export const readingSessionSchema = z.discriminatedUnion('status', [
  z.strictObject({ ...readingSessionBaseShape, status: z.literal('active') }),
  z.strictObject({ ...readingSessionBaseShape, status: z.literal('paused'), pausedAt: instantSchema }),
  z.strictObject({
    ...readingSessionBaseShape,
    status: z.literal('completed'),
    finishedAt: instantSchema,
    durationMs: z.int().min(0),
    endProgress: readingProgressSchema
  })
]).superRefine((session, context) => {
  if (session.status === 'completed' && session.startProgress.kind !== session.endProgress.kind) {
    context.addIssue({ code: 'custom', path: ['endProgress'], message: 'A sessão deve terminar na mesma unidade em que começou.' });
  }
  if (session.status === 'completed'
    && session.startProgress.kind === 'epub'
    && session.endProgress.kind === 'epub'
    && session.startProgress.documentSha256 !== session.endProgress.documentSha256) {
    context.addIssue({ code: 'custom', path: ['endProgress', 'documentSha256'], message: 'A sessão EPUB precisa permanecer no mesmo documento.' });
  }
  const startedMs = Date.parse(session.startedAt);
  const endMs = session.status === 'paused' ? Date.parse(session.pausedAt) : session.status === 'completed' ? Date.parse(session.finishedAt) : undefined;
  if (endMs !== undefined && endMs < startedMs) context.addIssue({ code: 'custom', message: 'A linha temporal da sessão é inválida.' });
  if (endMs !== undefined && session.accumulatedPausedMs > endMs - startedMs) {
    context.addIssue({ code: 'custom', path: ['accumulatedPausedMs'], message: 'A pausa acumulada ultrapassa a duração da sessão.' });
  }
});

export const statusHistorySchema = z.strictObject({
  id: entityIdSchema,
  bookId: entityIdSchema,
  fromStatus: bookStatusSchema.optional(),
  toStatus: bookStatusSchema,
  reason: z.enum(['initial', 'historical_import', 'manual', 'progress_completion', 'progress_correction_reopen', 'pause', 'abandon', 'resume']),
  changedAt: instantSchema,
  changedOn: localDateSchema,
  createdAt: instantSchema
});

const bookBaseShape = {
  id: entityIdSchema,
  title: z.string().trim().min(1).max(240),
  normalizedTitle: z.string().min(1),
  subtitle: z.string().trim().max(240).optional(),
  author: z.string().trim().min(1).max(200),
  normalizedAuthor: z.string().min(1),
  coverAssetId: entityIdSchema.optional(),
  isbn: z.string().trim().max(32).optional(),
  publisher: z.string().trim().max(160).optional(),
  publicationYear: z.int().min(0).max(3000).optional(),
  language: z.string().trim().max(80).optional(),
  genres: z.array(z.string().trim().min(1).max(80)),
  status: bookStatusSchema,
  seriesId: entityIdSchema.optional(),
  seriesNumber: z.number().positive().optional(),
  rating: z.number().min(0.5).max(5).multipleOf(0.5).optional(),
  review: z.string().max(20_000).optional(),
  completionOrigin: z.enum(['tracked', 'historical']).optional(),
  historicalCompletion: completionDateSchema.optional(),
  startedAt: instantSchema.optional(),
  startedOn: localDateSchema.optional(),
  finishedAt: instantSchema.optional(),
  finishedOn: localDateSchema.optional(),
  lastActivityAt: instantSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema
};

const paginatedBookSchema = z.strictObject({
  ...bookBaseShape,
  format: z.enum(['physical', 'ebook']),
  progress: pageProgressSchema
});

const bibleBookSchema = z.strictObject({
  ...bookBaseShape,
  format: z.enum(['physical', 'ebook']),
  progress: bibleChapterProgressSchema
});

const epubBookSchema = z.strictObject({
  ...bookBaseShape,
  format: z.literal('ebook'),
  progress: epubProgressSchema
});

const audioTimeBookSchema = z.strictObject({
  ...bookBaseShape,
  format: z.literal('audiobook'),
  progress: audioTimeProgressSchema
});

const audioPercentBookSchema = z.strictObject({
  ...bookBaseShape,
  format: z.literal('audiobook'),
  progress: audioPercentProgressSchema
});

export const bookSchema = z.union([
  paginatedBookSchema,
  bibleBookSchema,
  epubBookSchema,
  audioTimeBookSchema,
  audioPercentBookSchema
]).superRefine((book, context) => {
  const hasFinishedDates = Boolean(book.finishedAt && book.finishedOn);
  const historical = book.completionOrigin === 'historical';
  if (book.status === 'finished' && !historical && !hasFinishedDates) {
    context.addIssue({ code: 'custom', path: ['finishedAt'], message: 'Livro concluído precisa das datas de conclusão.' });
  }
  if (book.status !== 'finished' && (book.finishedAt !== undefined || book.finishedOn !== undefined)) {
    context.addIssue({ code: 'custom', path: ['finishedAt'], message: 'Livro não concluído não pode manter datas atuais de conclusão.' });
  }
  if (book.status === 'finished' && !book.completionOrigin) {
    context.addIssue({ code: 'custom', path: ['completionOrigin'], message: 'Livro concluido precisa identificar a origem da conclusao.' });
  }
  if (book.status !== 'finished' && book.completionOrigin !== undefined) {
    context.addIssue({ code: 'custom', path: ['completionOrigin'], message: 'Livro não concluído não pode manter origem de conclusão.' });
  }
  if (historical && (book.status !== 'finished' || !book.historicalCompletion)) {
    context.addIssue({ code: 'custom', path: ['historicalCompletion'], message: 'Leitura historica precisa estar concluida e informar a precisao da data.' });
  }
  if (historical && (book.finishedAt !== undefined || book.finishedOn !== undefined || book.startedAt !== undefined || book.startedOn !== undefined)) {
    context.addIssue({ code: 'custom', path: ['finishedAt'], message: 'Leitura historica nao pode inventar instantes de leitura.' });
  }
  if (!historical && book.historicalCompletion !== undefined) {
    context.addIssue({ code: 'custom', path: ['historicalCompletion'], message: 'Somente leitura historica pode manter data aproximada.' });
  }
  const isAtEnd = book.progress.kind === 'epub'
    ? false
    : book.progress.kind === 'pages'
    ? book.progress.totalPages !== undefined && book.progress.currentPage === book.progress.totalPages
    : book.progress.kind === 'audio_time'
      ? book.progress.currentMinutes === book.progress.totalMinutes
      : book.progress.kind === 'audio_percent'
        ? book.progress.currentPercent === 100
        : book.progress.completedChapterIds.length === BIBLE_TOTAL_CHAPTERS;
  if (book.status === 'finished' && !historical && book.progress.kind !== 'epub' && !isAtEnd) {
    context.addIssue({ code: 'custom', path: ['progress'], message: 'Livro concluído precisa estar no fim.' });
  }
});

export const locatorSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('page'), page: z.int().min(0) }),
  z.strictObject({ kind: z.literal('audio_time'), minutes: z.int().min(0) }),
  z.strictObject({ kind: z.literal('audio_percent'), percent: z.number().min(0).max(100) }),
  z.strictObject({
    kind: z.literal('epub_cfi'),
    cfi: epubCfiSchema,
    percent: z.number().min(0).max(100).optional(),
    documentSha256: documentSha256Schema
  })
]);

const noteShape = {
  id: entityIdSchema,
  bookId: entityIdSchema,
  content: z.string().trim().min(1).max(20_000),
  locator: locatorSchema.optional(),
  chapter: z.string().trim().max(200).optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema
};

export const noteSchema = z.strictObject(noteShape);
export const quoteSchema = z.strictObject(noteShape);

export const seriesSchema = z.strictObject({
  id: entityIdSchema,
  name: z.string().trim().min(1).max(180),
  normalizedName: z.string().min(1),
  description: z.string().max(2000).optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});

export const goalSchema = z.strictObject({
  id: entityIdSchema,
  year: z.int().min(1900).max(3000),
  bookTarget: z.int().positive().max(10_000),
  pageTarget: z.int().positive().optional(),
  minuteTarget: z.int().positive().optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});

export const settingsSchema = z.strictObject({
  id: z.literal('app'),
  theme: z.enum(['light', 'dark', 'system']),
  locale: z.literal('pt-BR'),
  showHomeHighlight: z.boolean(),
  bibleBookOrder: z.array(z.string()).refine(isValidBibleBookOrder, {
    message: 'O plano deve conter os 66 livros bíblicos, sem duplicatas ou ausências.'
  }).optional(),
  activeSessionId: entityIdSchema.optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});

export const serializedCoverAssetSchema = z.strictObject({
  id: entityIdSchema,
  dataBase64: z.string().min(1),
  mimeType: z.string().regex(/^image\/(?:jpeg|png|webp)$/),
  width: z.int().positive(),
  height: z.int().positive(),
  byteSize: z.int().nonnegative(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});

export const validateBook = (value: unknown) => bookSchema.parse(value);
